import _ from 'lodash';
import { IReactionDisposer, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Component } from 'react';

import { controller } from '../../core/Controllers/OrmController';
import { OrmStoreType } from '../../core/Stores/DirectoryStore';
import { OrmUserStoreType } from '../../core/Stores/OrmUserStore';
import { FormAlertStoreType } from '../../shared/Alerts/FormAlertStore';
import NatDirectoryFormContainer from '../../shared/Components/NatDirectoryFormContainer';
import { NavigationContainerStoreType } from '../../shared/ContainersStores/NavigationContainerStore';
import { catalog } from '../../shared/support/Catalog';
import { FormContainerPropsType, ProductKindType } from '../../shared/support/modelTypes';
import ProductKindForm from './ProductKindForm';
import { ProductKindFormStoreType } from './ProductKindFormStore';

interface PropsType extends FormContainerPropsType<ProductKindFormStoreType> {}

interface InjectedProps extends PropsType {
	navigationContainerStore: NavigationContainerStoreType;
	userStore: OrmUserStoreType;
	directoryStore: OrmStoreType;
	formAlertStore: FormAlertStoreType;
}

@inject('navigationContainerStore', 'userStore', 'directoryStore', 'formAlertStore')
@observer
class ProductKindFormContainer extends Component<PropsType> {
	formReactions: Array<IReactionDisposer>;
	constructor(props: PropsType) {
		super(props);
		this.formReactions = [];
		this.loadPage = this.loadPage.bind(this);
		this.makeReactions = this.makeReactions.bind(this);
		this.resetFormReactions = this.resetFormReactions.bind(this);
		this.fillEmptyRelations = this.fillEmptyRelations.bind(this);
		this.prepareModelForPost = this.prepareModelForPost.bind(this);
		this.validate = this.validate.bind(this);
		this.resetData = this.resetData.bind(this);
		this.updateList = this.updateList.bind(this);
		this.updateFeatures = this.updateFeatures.bind(this);
	}
	get injected() {
		return this.props as InjectedProps;
	}
	makeReactions() {
		this.resetFormReactions();
		_.forIn(this.props.store.model, (value, key) => {
			if (key !== 'isChanged') {
				this.formReactions.push(
					reaction(
						() => this.props.store.model[key],
						(value, previousValue) => {
							if (key !== 'accountingOption' && key !== 'unit') {
								if (value !== previousValue) {
									this.props.store.setValue(this.props.store, 'isChanged', true);
								}
							}
						}
					)
				);
			}
		});
		this.formReactions.push(
			reaction(
				() => this.props.store.model.accountingOptionId,
				(value) => {
					if (_.isEmpty(value)) {
						this.props.store.setValue(this.props.store.model, 'accountingOptionId', 'product_accounting_options.standart');
						this.props.store.setValue(this.props.store.model, 'accountingOption', {
							description: null,
							id: 'product_accounting_options.standart',
							keyId: 'standart',
							name: { ru: 'Стандартный' },
							ownerId: 'product_accounting_options',
							predefined: true,
							predefinedName: 'ProductAccountingOptionsStandart',
							predefinedVersion: 0
						});
					}
				}
			)
		);
		this.formReactions.push(
			reaction(
				() => this.props.store.model.useFeatureOptionId,
				(value) => {
					if (_.isEmpty(value)) {
						this.props.store.setValue(this.props.store.model, 'useFeatureOptionId', 'use_feature_options.not_used');
						this.props.store.setValue(this.props.store.model, 'useFeatureOption', {
							id: 'use_feature_options.not_used',
							keyId: 'not_used',
							ownerId: 'use_feature_options',
							name: { ru: 'Не используется' },
							description: null,
							predefined: true,
							predefinedName: 'UseFeatureOptionsNotUsed',
							predefinedVersion: 0
						});
					}
				}
			)
		);
		this.formReactions.push(
			reaction(
				() => this.props.store.isChanged,
				(value, previousValue) => {
					if (value !== previousValue) {
						catalog.generateTitle(this.props.navigationRoute, 'OrmCatalog', 'form');
					}
				}
			)
		);
	}
	loadPage(elementId: string, findModel: (elementId: string) => void) {
		document.title = 'Журнал';
		if (elementId !== 'new') {
			findModel(elementId);
		} else {
			this.props.store.setValue(this.props.store, 'responseCode', 200);
			catalog.generateTitle(this.props.navigationRoute, 'OrmCatalog', 'form');
			this.props.store.setValue(this.props.store, 'isChanged', true);
		}
	}
	fillEmptyRelations(model: ProductKindType) {
		if (model.accountingOptionId === 'product_accounting_options.standart' && _.isEmpty(model.accountingOption)) {
			model.accountingOption = _.cloneDeep(this.props.store.model.accountingOption);
		}
		if (model.useFeatureOptionId === 'use_feature_options.not_used' && _.isEmpty(model.useFeatureOption)) {
			model.useFeatureOption = _.cloneDeep(this.props.store.model.useFeatureOption);
		}
		return model;
	}
	prepareModelForPost(model: ProductKindType) {
		delete model.accountingOption;
		delete model.unit;
		delete model.useFeatureOption;
		return model;
	}
	validate() {
		return !_.isEmpty(this.props.store.model.name[this.injected.directoryStore.models.language]);
	}
	resetFormReactions() {
		this.formReactions.forEach((dispose) => dispose());
		this.formReactions = [];
	}
	resetData() {
		this.props.store.setValue(this.props.store, 'list', []);
	}
	updateList(model: ProductKindType) {
		this.updateFeatures(model);
	}
	updateFeatures(model: ProductKindType) {
		this.props.store.setValue(this.props.store, 'isLoading', true);
		controller
			.fetchProductKindFeatures(model)
			.then((data) => {
				if (!_.isEmpty(data)) {
					this.props.store.setValue(this.props.store.productFeatures, 'list', data);
				}
				this.props.store.setValue(this.props.store, 'isLoading', false);
			})
			.catch((error) => {
				catalog.handleNatError(error);
				this.props.store.setValue(this.props.store, 'isLoading', false);
			});
	}
	render() {
		return (
			<NatDirectoryFormContainer
				index={this.props.index}
				navigationRoute={this.props.navigationRoute}
				replace={this.props.replace}
				makeReactions={this.makeReactions}
				resetFormReactions={this.resetFormReactions}
				loadPage={this.loadPage}
				fillEmptyRelations={this.fillEmptyRelations}
				validate={this.validate}
				prepareModelForPost={this.prepareModelForPost}
				store={this.props.store}
				getChildMethod={this.props.getChildMethod}
				updateList={this.updateList}
				executeAfterFinding={this.updateFeatures}
				parentRoute="/productKinds/"
				handlers={this.props.handlers}>
				{(childrenProps) => (
					<ProductKindForm
						removeNavigationRoute={this.props.removeNavigationRoute}
						productKindFormStore={this.props.store}
						navigationRoute={this.props.navigationRoute}
						goTo={this.props.goTo}
						childrenProps={childrenProps}
						resetData={this.resetData}
					/>
				)}
			</NatDirectoryFormContainer>
		);
	}
}

export default ProductKindFormContainer;
